/** Everyone gets one personal connection, on every tier. */
export const PERSONAL_CONNECTION_ALLOWANCE = 1;
const ANALYTICS = [
    'benchmarks.limited',
    'benchmarks.full',
    'alerts.undercut',
    'demand.heatmap',
    'restock',
    'trends.90d',
];
/**
 * Store-owner tiers, gated on how much shop there is to analyse rather than on
 * which features are withheld. The named steps deliberately sit close enough
 * together that nobody has to jump 4x in price for one more location; the
 * add-ons below cover the gaps between them.
 */
export const PLANS = {
    free: {
        tier: 'free',
        name: 'Free',
        price: 0,
        cycleDays: 30,
        blurb: 'Where-to-buy search, plus a taste of the analytics on a small shop.',
        maxChestshops: 5,
        maxLocations: 1,
        benchmarkItemLimit: 5,
        historyDays: 7,
        firmLimit: 1,
        features: ['benchmarks.limited'],
    },
    wood: {
        tier: 'wood',
        name: 'Wood',
        price: 360,
        listPrice: 400,
        cycleDays: 30,
        blurb: 'Full analytics and alerts for a single-location shop.',
        maxChestshops: 50,
        maxLocations: 1,
        benchmarkItemLimit: null,
        historyDays: 90,
        firmLimit: 1,
        features: [...ANALYTICS],
    },
    iron: {
        tier: 'iron',
        name: 'Iron',
        price: 1200,
        listPrice: 1500,
        cycleDays: 30,
        blurb: 'Several locations, with everything Wood includes.',
        maxChestshops: 250,
        maxLocations: 3,
        benchmarkItemLimit: null,
        historyDays: 90,
        firmLimit: 3,
        features: [...ANALYTICS, 'portfolio'],
    },
    gold: {
        tier: 'gold',
        name: 'Gold',
        price: 2280,
        listPrice: 3000,
        cycleDays: 30,
        blurb: 'Adds the opportunity finder, pricing recommendations and portfolio analytics.',
        maxChestshops: 500,
        maxLocations: 5,
        benchmarkItemLimit: null,
        historyDays: 365,
        firmLimit: 5,
        features: [...ANALYTICS, 'portfolio', 'opportunity', 'recommendations', 'alerts.discord'],
    },
    diamond: {
        tier: 'diamond',
        name: 'Diamond',
        price: 4200,
        listPrice: 6000,
        cycleDays: 30,
        blurb: 'Large multi-location operations, with exportable reports.',
        maxChestshops: 1000,
        maxLocations: 10,
        benchmarkItemLimit: null,
        historyDays: 365,
        firmLimit: 10,
        features: [
            ...ANALYTICS,
            'portfolio',
            'opportunity',
            'recommendations',
            'alerts.discord',
            'export',
        ],
    },
    netherite: {
        tier: 'netherite',
        name: 'Netherite',
        price: 7200,
        listPrice: 12000,
        cycleDays: 30,
        blurb: 'Everything, uncapped, with priority refresh and support.',
        maxChestshops: null,
        maxLocations: null,
        benchmarkItemLimit: null,
        historyDays: 365,
        firmLimit: 25,
        features: [
            ...ANALYTICS,
            'portfolio',
            'opportunity',
            'recommendations',
            'alerts.discord',
            'export',
            'priority',
        ],
    },
};
/** Tiers in order, for upgrade prompts and pricing tables. */
export const TIER_ORDER = ['free', 'wood', 'iron', 'gold', 'diamond', 'netherite'];
/**
 * Fine-tuning between named tiers, so a shop that is 60 signs over Wood does
 * not have to buy Iron outright.
 */
export const ADD_ONS = {
    extraLocation: { price: 400, unit: 'per extra location per month' },
    extraChestshops: { price: 250, blockSize: 50, unit: 'per extra 50 chestshops per month' },
};
/** First month free at Wood, for everyone, at launch. */
export const LAUNCH_PROMO = {
    tier: 'wood',
    months: 1,
    blurb: 'First month free on Wood - no payment needed to try the full analytics.',
};
/**
 * Monthly price including add-ons.
 *
 * Add-on chestshops are sold in blocks, so 10 extra signs costs the same as 50 -
 * rounding up is the whole point of a block, and pricing them individually would
 * undercut the tier above for no gain.
 */
export function monthlyPrice(tier, extraChestshops = 0, extraLocations = 0) {
    const blocks = Math.ceil(Math.max(0, extraChestshops) / ADD_ONS.extraChestshops.blockSize);
    return (PLANS[tier].price +
        blocks * ADD_ONS.extraChestshops.price +
        Math.max(0, extraLocations) * ADD_ONS.extraLocation.price);
}
/**
 * How much a tier is currently reduced, as whole percent, or null when it is
 * at its normal price.
 *
 * Rounded rather than truncated so a 24% cut does not advertise itself as 23%,
 * and computed from the two real prices so the badge can never drift from the
 * figures beside it.
 */
export function discountPct(plan) {
    if (plan.listPrice == null || plan.listPrice <= plan.price)
        return null;
    return Math.round(((plan.listPrice - plan.price) / plan.listPrice) * 100);
}
/** The largest discount on offer, for a headline. Null when nothing is reduced. */
export function bestDiscountPct() {
    const cuts = Object.values(PLANS)
        .map(discountPct)
        .filter((pct) => pct != null);
    return cuts.length > 0 ? Math.max(...cuts) : null;
}
export function planHasFeature(tier, feature) {
    return PLANS[tier].features.includes(feature);
}
/** The cheapest tier that covers a shop of this size. */
export function smallestTierFor(chestshops, locations) {
    for (const tier of TIER_ORDER) {
        const plan = PLANS[tier];
        const shopsOk = plan.maxChestshops == null || chestshops <= plan.maxChestshops;
        const locationsOk = plan.maxLocations == null || locations <= plan.maxLocations;
        if (shopsOk && locationsOk)
            return tier;
    }
    return 'netherite';
}
/**
 * What a role can see. Tabs are decided by role first and tier second: a buyer
 * has no shop, so hiding analytics from them is not an upsell, it is just
 * removing clutter for something they will never use.
 */
export const ROLE_TABS = {
    buyer: ['where-to-buy'],
    store_owner: [
        'where-to-buy',
        'overview',
        'my-shop',
        'pricing',
        'demand',
        'restock',
        'opportunities',
        'performance',
        'sales',
        'alerts',
        'connections',
        'billing',
    ],
    delivery: ['where-to-buy', 'delivery'],
};
export function roleCanSee(role, tab) {
    return ROLE_TABS[role].includes(tab);
}
/** Default thresholds for the insight engine; overridable per subscriber. */
export const DEFAULTS = {
    /** Absolute percent delta from benchmark before an item is called mispriced. */
    mispricingThresholdPct: 10,
    /** Market must sit this far below your price before an undercut alert fires. */
    undercutThresholdPct: 8,
    /** A benchmark below this sample size is flagged low-confidence. */
    minSampleSize: 8,
    /** Distinct sellers needed before a benchmark counts as network-grade. */
    minSellerCount: 3,
    /** A benchmark older than this is stale. */
    benchmarkMaxAgeHours: 6,
    /** No sales for this many multiples of the normal gap means likely stockout. */
    stockoutGapMultiplier: 3,
    /** Minimum trailing sales before velocity is trusted for stockout detection. */
    minSalesForVelocity: 4,
    benchmarkWindowDays: 30,
    /** Elasticity assumed by the pricing recommender absent a fitted curve. */
    assumedPriceElasticity: -1.4,
};
/**
 * Seed catalogue. Item codes follow the Minecraft material convention the
 * ChestShop sign uses; the display name and category are ours.
 */
export const ITEM_CATALOG = [
    { code: 'DIAMOND', displayName: 'Diamond', category: 'Ores & Gems' },
    { code: 'EMERALD', displayName: 'Emerald', category: 'Ores & Gems' },
    { code: 'GOLD_INGOT', displayName: 'Gold Ingot', category: 'Ores & Gems' },
    { code: 'IRON_INGOT', displayName: 'Iron Ingot', category: 'Ores & Gems' },
    { code: 'COPPER_INGOT', displayName: 'Copper Ingot', category: 'Ores & Gems' },
    { code: 'NETHERITE_INGOT', displayName: 'Netherite Ingot', category: 'Ores & Gems' },
    { code: 'ANCIENT_DEBRIS', displayName: 'Ancient Debris', category: 'Ores & Gems' },
    { code: 'COAL', displayName: 'Coal', category: 'Ores & Gems' },
    { code: 'LAPIS_LAZULI', displayName: 'Lapis Lazuli', category: 'Ores & Gems' },
    { code: 'QUARTZ', displayName: 'Nether Quartz', category: 'Ores & Gems' },
    { code: 'AMETHYST_SHARD', displayName: 'Amethyst Shard', category: 'Ores & Gems' },
    { code: 'REDSTONE', displayName: 'Redstone Dust', category: 'Redstone' },
    { code: 'SLIME_BALL', displayName: 'Slimeball', category: 'Redstone' },
    { code: 'OAK_LOG', displayName: 'Oak Log', category: 'Wood' },
    { code: 'SPRUCE_LOG', displayName: 'Spruce Log', category: 'Wood' },
    { code: 'STONE', displayName: 'Stone', category: 'Building' },
    { code: 'COBBLESTONE', displayName: 'Cobblestone', category: 'Building' },
    { code: 'GLASS', displayName: 'Glass', category: 'Building' },
    { code: 'SAND', displayName: 'Sand', category: 'Building' },
    { code: 'CONCRETE_POWDER', displayName: 'Concrete Powder', category: 'Building' },
    { code: 'WHEAT', displayName: 'Wheat', category: 'Farming' },
    { code: 'CARROT', displayName: 'Carrot', category: 'Farming' },
    { code: 'POTATO', displayName: 'Potato', category: 'Farming' },
    { code: 'SUGAR_CANE', displayName: 'Sugar Cane', category: 'Farming' },
    { code: 'BONE_MEAL', displayName: 'Bone Meal', category: 'Farming' },
    { code: 'COOKED_BEEF', displayName: 'Steak', category: 'Food' },
    { code: 'GOLDEN_CARROT', displayName: 'Golden Carrot', category: 'Food' },
    { code: 'ENCHANTED_GOLDEN_APPLE', displayName: 'Enchanted Golden Apple', category: 'Food' },
    { code: 'HONEY_BOTTLE', displayName: 'Honey Bottle', category: 'Food' },
    { code: 'ENDER_PEARL', displayName: 'Ender Pearl', category: 'Combat' },
    { code: 'TOTEM_OF_UNDYING', displayName: 'Totem of Undying', category: 'Combat' },
    { code: 'GUNPOWDER', displayName: 'Gunpowder', category: 'Combat' },
    { code: 'BLAZE_ROD', displayName: 'Blaze Rod', category: 'Brewing' },
    { code: 'GHAST_TEAR', displayName: 'Ghast Tear', category: 'Brewing' },
    { code: 'NETHER_WART', displayName: 'Nether Wart', category: 'Brewing' },
    { code: 'SHULKER_SHELL', displayName: 'Shulker Shell', category: 'Utility' },
    { code: 'ELYTRA', displayName: 'Elytra', category: 'Utility' },
    { code: 'EXPERIENCE_BOTTLE', displayName: 'Bottle of Enchanting', category: 'Utility' },
    { code: 'PRISMARINE_SHARD', displayName: 'Prismarine Shard', category: 'Utility' },
    { code: 'SPONGE', displayName: 'Sponge', category: 'Utility' },
];
export const ITEM_BY_CODE = new Map(ITEM_CATALOG.map((i) => [i.code, i]));
export function itemName(code) {
    return ITEM_BY_CODE.get(code)?.displayName ?? code;
}
export function itemCategory(code) {
    return ITEM_BY_CODE.get(code)?.category ?? 'Uncategorised';
}
/** Districts used for the opportunity finder locality signal. */
export const DISTRICTS = [
    'Aventura',
    'Reveille',
    'Hamilton',
    'Willowdale',
    'Oakridge',
    'Beckett',
];
export const CURRENCY_SYMBOL = '$';
export function formatCurrency(value) {
    return (CURRENCY_SYMBOL +
        value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
}
export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
//# sourceMappingURL=constants.js.map